import React, { useState, useEffect, useRef } from "react";
import "./HomePage.css";
import { Link } from "react-router-dom";
import { useAnimation } from "../context/AnimationContext";
import { Palette, Workflow, Briefcase, Code, Building2 } from "lucide-react";
import MetaTags from "../components/MetaTags";
import HiringBanner from "../components/HiringBanner";
import { HIRING_BANNER_DATA } from "../utils/seedData";
import SectionTitle from "../components/SectionTitle";
import PickupCard from "../components/PickupCard";
import MediaCard from "../components/MediaCard";
import BeliefSection from "../components/BeliefSection/BeliefSection";
import LibrarySection from "../components/LibrarySection";
import FeatureSection from "../components/FeatureSection";
import JobCard from "../components/JobCard";
import RolesSection from "../components/RolesSection";
import InterviewHomeSection from "../components/InterviewHomeSection";
import { useSNSContentByType } from "../hooks/useSNSContent";
import { useDragScroll } from "../hooks/useDragScroll";
import {
  ALL_JOBS_URL,
  ACCOUNTING_URL,
  DEVELOPMENT_ENGINEER_URL,
  DX_ENGINEER_URL,
  HR_URL,
  INFRASTRUCTURE_ENGINEER_URL,
  MANAGEMENT_DIRECTOR_URL,
  MARKETER_URL,
  SALES_INTERNSHIP_URL,
} from "../config/careerLinks";

const HomePage = () => {
  // const [showBackToNewGraduate, setShowBackToNewGraduate] = useState(false);
  const { animationEnabled } = useAnimation();
  const videoRef = useRef(null);
  const mediaScrollRef = useDragScroll({ scrollSpeed: 1.5 });
  // Initialize isMobile immediately to prevent flash
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(max-width: 768px)").matches;
    }
    return false;
  });

  // Newest approved YouTube / note / X from Firestore (scheduler refreshes, no page-load API call)
  const {
    xPosts: apiXPosts,
    youtubeVideos: apiYoutubeVideos,
    noteArticles: apiNoteArticles
  } = useSNSContentByType(2, false, false);

  // Fallback mock data in case API hasn't been called yet
  const fallbackYoutubeVideos = [
    {
      id: "yt-1",
      title: "【密着】エンジニアの成長を支えるキャリア管理部のリアルな1日",
      thumbnail: "https://i.ytimg.com/vi/4ppI0H2Fsow/maxresdefault.jpg",
      url: "https://youtu.be/4ppI0H2Fsow",
      date: new Date("2024-01-15"),
    },
    {
      id: "yt-2",
      title: "FLARETECHエンジニアインタビュー",
      thumbnail: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=450&fit=crop",
      url: "https://www.youtube.com/@FLARETECH_saiyo",
      date: new Date("2024-01-10"),
    },
  ];

  const fallbackNoteArticles = [
    {
      id: "note-1",
      title:
        "『面白いことがしたい』から始まった30代エンジニア転職—飲食業界での課題解決体験が人生を変えた—",
      thumbnail:
        "https://assets.st-note.com/production/uploads/images/314829890/rectangle_large_type_2_089e2cde2e573870b81010bc8d0d7ccf.png?width=1280",
      url: "https://note.com/flaretech_it/n/n207e25a97f11",
      date: new Date("2024-01-12"),
    },
  ];

  const fallbackXPosts = [
    {
      id: "temp-x-1",
      text: "FLARETECHでは一緒に働く仲間を募集しています！",
      thumbnail: null,
      url: "https://twitter.com/FLARETECH_saiyo",
      date: new Date("2024-01-14"),
    },
  ];

  // Use API data if available, otherwise use fallback
  const youtubeVideos = apiYoutubeVideos?.length > 0 ? apiYoutubeVideos : fallbackYoutubeVideos;
  const noteArticles = apiNoteArticles?.length > 0 ? apiNoteArticles : fallbackNoteArticles;
  const xPosts = apiXPosts?.length > 0 ? apiXPosts : fallbackXPosts;

  // Helper function to format dates consistently
  const formatDate = (date) => {
    if (!date) return "";
    // If date is already a Date object, use it; otherwise create one
    const dateObj = date instanceof Date ? date : new Date(date);
    // Check if valid date
    if (isNaN(dateObj.getTime())) return "";
    return dateObj
      .toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\//g, ".");
  };

  // useEffect(() => {
  //   // 新卒ページを訪問したかどうかをsessionStorageからチェック
  //   const visitedNewGraduate = sessionStorage.getItem('visitedNewGraduate');
  //   if (visitedNewGraduate === 'true') {
  //     setShowBackToNewGraduate(true);
  //   }
  // }, []);

  // Detect mobile screen size
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleMediaChange = (e) => {
      setIsMobile(e.matches);
      // Force video reload when screen size changes
      if (videoRef.current) {
        videoRef.current.load();
        if (animationEnabled) {
          videoRef.current.play().catch(() => {
            // Silently handle autoplay prevention
          });
        }
      }
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener("change", handleMediaChange);

    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, [animationEnabled]);

  // Control video playback based on animation toggle
  useEffect(() => {
    if (videoRef.current) {
      if (animationEnabled) {
        videoRef.current.play().catch(() => {
          // Silently handle autoplay failures
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [animationEnabled]);

  // Suppress script errors from browser extensions and third-party scripts
  useEffect(() => {
    const handleError = (event) => {
      // Suppress generic script errors (usually from extensions)
      if (
        event.message === "Script error." ||
        !event.filename ||
        event.filename.includes("extension://")
      ) {
        event.stopPropagation();
        event.preventDefault();
        return false;
      }
    };
    window.addEventListener("error", handleError, true);
    return () => window.removeEventListener("error", handleError, true);
  }, []);

  const jobCards = [
    {
      titleEn: "Engineer",
      titleJa: "開発エンジニア",
      desc: "技術力で経営課題を解決する仕事。",
      path: DEVELOPMENT_ENGINEER_URL,
      linkUrl: "/jobs/development-engineer",
      seniorsUrl: "/interview?job=engineer",
      image: "/images/homepage/roles-section/4.jpg",
    },
    {
      titleEn: "Infrastructure Engineer",
      titleJa: "インフラエンジニア",
      desc: "安定と進化を両立させる専門家。",
      path: INFRASTRUCTURE_ENGINEER_URL,
      linkUrl: "/jobs/infrastructure-engineer",
      seniorsUrl: "/interview?job=infra",
      image: "/images/team/yokochi-back.jpg",
    },
    {
      titleEn: "DX Engineer",
      titleJa: "DXエンジニア",
      desc: "業務の本質を変える技術職。",
      path: DX_ENGINEER_URL,
      linkUrl: "/jobs/dx-engineer",
      seniorsUrl: "/interview?job=dx",
      image: "/images/homepage/roles-section/3.jpg",
    },
    {
      titleEn: "Sales",
      titleJa: "営業",
      desc: "エンジニアと企業をつなぐ重要ポジション。",
      path: ALL_JOBS_URL,
      linkUrl: "/jobs/sales",
      seniorsUrl: "/interview?job=sales",
      image: "/images/team/nakagama-back.jpg",
    },
  ];

  return (
    <div className="home-page">
      <MetaTags
        title="FLARETECH 採用サイト - エンジニアの市場価値を高めるテックカンパニー"
        description="FLARETECHの採用情報サイト。エンジニアの市場価値を高めるテックカンパニー。UI/UXデザイナー、エンジニア、プロダクトマネージャーなど、様々な職種で一緒に働く仲間を募集しています。"
        keywords="FLARETECH,採用,デザイナー,エンジニア,UI/UX,プロダクトマネージャー,フレアテック,キャリア,テックカンパニー"
        path="/"
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster={isMobile ? "/hero-career-poster-mobile.jpg" : "/hero-career-poster.jpg"}
            src={isMobile ? "/hero-video-career-mobile.mp4" : "/videos/homepage/hero-desktop.mp4"}
          ></video>
        </div>
        <div className="hero-overlay">
          {/* {showBackToNewGraduate && (
            <Link to="/newgraduate" className="hero-back-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              新卒に戻る
            </Link>
          )} */}
          <div className="hero-content">
            <h1 className="hero-title">エンジニアの市場価値を高めるテックカンパニー</h1>
            <p className="hero-subtitle">ようこそ、フレアテックの採用サイトへ</p>
            <div className="hero-buttons">
              <a
                href={ALL_JOBS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-entry-btn"
                aria-label="応募する - 13件募集中"
              >
                <span className="btn-text-main">Entry</span>
                <span className="btn-text-sub">13件</span>
                <svg viewBox="0 0 10 10" fill="currentColor">
                  <path d="M5.845 2.22a.75.75 0 0 1 1.06 0L9.23 4.543c.41.41.41 1.075 0 1.485L6.905 8.352a.75.75 0 0 1-1.06-1.061l1.25-1.251H.75a.75.75 0 0 1 0-1.5h6.354l-1.26-1.26a.75.75 0 0 1 0-1.06Z" />
                </svg>
              </a>
              <a
                href={SALES_INTERNSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-shinsotsu-btn"
                aria-label="新卒採用 - 3職種募集中"
              >
                <span className="btn-text-main">新卒</span>
                <span className="btn-text-sub">3職種</span>
                <svg viewBox="0 0 10 10" fill="currentColor">
                  <path d="M5.845 2.22a.75.75 0 0 1 1.06 0L9.23 4.543c.41.41.41 1.075 0 1.485L6.905 8.352a.75.75 0 0 1-1.06-1.061l1.25-1.251H.75a.75.75 0 0 1 0-1.5h6.354l-1.26-1.26a.75.75 0 0 1 0-1.06Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section className="certification-section">
        <div className="certification-container">
          <h2 className="certification-title">Certification</h2>
          <p className="certification-subtitle">認定</p>
          <div className="certification-awards">
            <div className="certification-award">
              <img
                src="/images/GPTlogo.jpg"
                alt="Great Place to Work"
                className="certification-logo"
              />
              <p className="certification-caption">
                Great Place to Work Institute of
                Japanが実施する調査において、「働きがいのある会社」に認定されました
              </p>
              <p className="certification-caption certification-award-name">GPTW</p>
            </div>
            <div className="certification-award">
              <a
                href="https://best100.v-tsushin.jp/company/flaretech/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/images/bv100-2026-logo-1294x964.png"
                  alt="Best Venture 100"
                  className="certification-logo"
                />
              </a>
              <p className="certification-caption">これから成長が期待される100社に選出されました</p>
              <p className="certification-caption certification-award-name">ベストベンチャー100</p>
            </div>
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <RolesSection
        title="Member"
        description="FLARETECHの価値を創るメンバーをご紹介します"
        jobs={jobCards}
        buttonText="Entry"
        linkText="この職種の魅力"
      />

      <InterviewHomeSection />

      {/* Shain Voice Banner */}
      <section className="shain-voice-banner-section">
        <div className="shain-voice-banner-container">
          <p className="shain-voice-text">社員からのリアルな声をこちらのサイトで公開中</p>
          <a
            href="https://x.gd/e1VJu"
            target="_blank"
            rel="noreferrer noopener"
            className="shain-voice-link"
          >
            <img
              src="/images/shain-voice-banner.png"
              alt="社員の声"
              className="shain-voice-image"
            />
          </a>
        </div>
      </section>

      {/* What We Believe */}
      <BeliefSection />

      {/* About Section */}
      <FeatureSection
        video="/videos/homepage/about.mp4"
        imageAlt="FLARETECH会社概要 - 1ページで分かるフレアテック"
        label="About"
        sublabel="フレアテックの基本"
        title="1ページで分かるフレアテック"
        description="知っておくといい基本情報を1ページにまとめました。フレアテックを知る入口としてご覧ください。"
        buttonText="View Overview"
        buttonLink="/about/summary"
        animationEnabled={animationEnabled}
        tags={[
          { text: "事業", link: "/about/business" },
          { text: "フレアテックの噂", link: "/about/prerequisites" },
          { text: "生成AI活用の現状", link: "/about/ai" },
          { text: "ストーリー", link: "/about/story" },
          { text: "未来", link: "/about/future" },
          { text: "歴史", link: "/about/experience-shift" },
          { text: "経営陣", link: "/about/leadership" },
          { text: "もっと知るリンク集", link: "/about/more" },
        ]}
      />

      {/* Culture Section */}
      <FeatureSection
        video="/videos/homepage/culture.mp4"
        imageAlt="FLARETECHカルチャー - バリューと働き方"
        label="Culture"
        sublabel="フレアテックの文化"
        title="バリューとカルチャー"
        description="フレアテックが大切にしている価値観と実際の働き方をご紹介します。"
        buttonText="View Culture"
        buttonLink="/culture/vision-mission"
        animationEnabled={animationEnabled}
        tags={[
          { text: "Purpose / Mission / Vision", link: "/culture/vision-mission" },
          { text: "バリュー", link: "/culture/values" },
          { text: "プリンシプル", link: "/culture/principles" },
          { text: "社内用語", link: "/culture/keywords" },
          { text: "ナレッジ", link: "/culture/knowledge" },
        ]}
      />

      {/* People Section */}
      <FeatureSection
        video="/videos/homepage/people.mp4"
        imageAlt="FLARETECHのメンバー - 社員インタビュー"
        label="People"
        sublabel="フレアテックの仲間"
        title="メンバー紹介"
        description="様々なバックグラウンドを持つメンバーがフレアテックで活躍しています。"
        buttonText="View People"
        buttonLink="/people"
        animationEnabled={animationEnabled}
        tags={[
          { text: "社員インタビュー", link: "/interview" },
        ]}
      />

      {/* Workstyle Section */}
      <FeatureSection
        video="/videos/homepage/workstyle.mp4"
        imageAlt="FLARETECHの働き方調査 - フレックスタイム制度とリモートワーク"
        label="Workstyle"
        sublabel="フレアテックの環境"
        title="社員アンケート"
        description="約210名の社員に対して率直な意見を募ったアンケート結果を公開しています。"
        buttonText="View Survey"
        buttonLink="/workstyle/survey"
        animationEnabled={animationEnabled}
        tags={[
          { text: "Organization Design", link: "/workstyle/px" },
          { text: "オンボーディング", link: "/workstyle/onboarding" },
          { text: "育成・学習機会", link: "/workstyle/development" },
          { text: "評価と報酬", link: "/workstyle/evaluation" },
          { text: "キャリアパス", link: "/workstyle/career-path" },
          { text: "福利厚生", link: "/workstyle/benefits" },
        ]}
      />

      {/* Process Section */}
      <FeatureSection
        video="/videos/homepage/process.mp4"
        imageAlt="FLARETECH採用プロセス - 選考フローとステップ"
        label="Process"
        sublabel="フレアテックの選考"
        title="選考プロセス"
        description="フレアテックの選考の面接回数や期間などについて詳しくご説明しています。"
        buttonText="View Hiring"
        buttonLink="/process/process"
        animationEnabled={animationEnabled}
        tags={[
          { text: "リエントリー制度", link: "/process/reentry" },
          { text: "アルムナイ採用", link: "/process/alumni" },
          { text: "よくある質問（Q&A）", link: "/process/faq" },
        ]}
      />

      {/* Media Section */}
      <section className="media-section">
        <div className="media-container">
          <SectionTitle title="Media" />
          <div className="media-scroll-container" ref={mediaScrollRef}>
            <div className="media-cards-wrapper">
              <MediaCard
                title="CEO×5のベストベンチャー100 視座・視点インタビュー"
                subtitle="CEO VOICE"
                logo="ceo-voice"
                thumbnail="/images/media/marvel-01.jpg"
                link="https://www.ceo-voice.com/interview/marvel.html"
                backgroundColor="#ffffff"
              />
              <MediaCard
                title="コソッと成長を支える、古都鎌倉のIT企業の挑戦"
                subtitle="ベンチャーJP"
                logo="venture"
                thumbnail="/images/media/IMG_5717-scaled.jpg"
                link="https://venture.jp/news/2023/05/25/2783/"
                backgroundColor="#ffffff"
              />
              <MediaCard
                title="エンジニアの成長を支える。ブレアテックのキャリア管理部のリアルな1日"
                subtitle="ベンチャー通信"
                logo="v-tsushin"
                thumbnail="/images/media/VT94_flaretech_banner_300x250.png"
                link="https://v-tsushin.jp/interview/vt94_flaretech/"
                backgroundColor="#ffffff"
              />
              <MediaCard
                title="ベストベンチャー100 視座・視点インタビュー"
                subtitle="YOUTRUST"
                logo="youtrust"
                thumbnail="/images/media/youtrust-flaretech.png"
                link="https://youtrust.jp/studio/articles/flaretech"
                backgroundColor="#ffffff"
              />
              <MediaCard
                title="CEO×若手社員 キャリアと成長を支える対談"
                subtitle="YouTube"
                logo="youtube"
                thumbnail="/images/media/youtube-c-hX3zLBc54.jpg"
                link="https://youtu.be/c-hX3zLBc54?si=0QbtwT0H3Js5RpOj"
                backgroundColor="#ffffff"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pick Up Section */}
      <section className="pickup-section">
        <div className="pickup-container">
          <SectionTitle title="Pick Up" />
          <div className="pickup-cards-grid">
            {/* YouTube Videos */}
            {youtubeVideos.slice(0, 2).map((video, index) => (
              <PickupCard
                key={`youtube-${video.id}`}
                type="youtube"
                variant="glassmorphism"
                header="YouTube"
                thumbnail={{
                  type: "image",
                  src: video.thumbnail,
                }}
                text={video.title}
                date={formatDate(video.date)}
                link={video.url}
                showLinkIcon={true}
              />
            ))}

            {/* Note Article */}
            {noteArticles.length > 0 && (
              <PickupCard
                type="note"
                variant="glassmorphism"
                header="note"
                thumbnail={{
                  type: noteArticles[0].thumbnail ? "image" : "note-style",
                  src: noteArticles[0].thumbnail,
                  text: noteArticles[0].title,
                }}
                text={noteArticles[0].title}
                date={formatDate(noteArticles[0].date)}
                link={noteArticles[0].url}
                showLinkIcon={true}
              />
            )}

            {/* X Post */}
            {xPosts.length > 0 && (
              <PickupCard
                type="x"
                variant="glassmorphism"
                header="X"
                thumbnail={{
                  type: xPosts[0].thumbnail ? "image" : "x-style",
                  src: xPosts[0].thumbnail,
                }}
                text={xPosts[0].text}
                date={formatDate(xPosts[0].date)}
                link={xPosts[0].url}
                showLinkIcon={true}
              />
            )}
          </div>
        </div>
      </section>

      {/* FLARETECH Library Section */}
      <LibrarySection companyName="FLARETECH" />

      {/* Hiring Banner Section */}
      <HiringBanner {...HIRING_BANNER_DATA} />

      {/* Job Listings Section */}
      <section className="job-listings-section">
        <div className="job-listings-container">
          {/* Engineering */}
          <div className="job-category">
            <h3 className="job-category-title">Engineering</h3>
            <div className="job-list">
              <a
                href={DEVELOPMENT_ENGINEER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Code size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］開発エンジニア</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={INFRASTRUCTURE_ENGINEER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Code size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］インフラエンジニア</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={DX_ENGINEER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Code size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］DXエンジニア</span>
                <span className="job-arrow">↗</span>
              </a>
            </div>
          </div>

          {/* Corporate & HR */}
          <div className="job-category">
            <h3 className="job-category-title">Corporate & HR</h3>
            <div className="job-list">
              <a
                href={MARKETER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Building2 size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］マーケター職</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={MANAGEMENT_DIRECTOR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Building2 size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］管理部長</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={HR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Building2 size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］人事担当</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={ACCOUNTING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Building2 size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［中途］経理担当</span>
                <span className="job-arrow">↗</span>
              </a>
            </div>
          </div>

          {/* New Graduate */}
          <div className="job-category">
            <h3 className="job-category-title">新卒採用</h3>
            <div className="job-list">
              <a
                href={SALES_INTERNSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Briefcase size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［新卒］営業長期インターンシップ</span>
                <span className="job-arrow">↗</span>
              </a>
              {/* <a
                href={SALES_INTERNSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Briefcase size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［新卒2027］エンジニア</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={SALES_INTERNSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Briefcase size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［新卒2027］営業職</span>
                <span className="job-arrow">↗</span>
              </a>
              <a
                href={SALES_INTERNSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="job-item"
              >
                <span className="job-icon">
                  <Briefcase size={18} strokeWidth={2} />
                </span>
                <span className="job-name">［新卒2026］営業職</span>
                <span className="job-arrow">↗</span>
              </a> */}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
